import { LegalPageLayout } from '../components/legal/LegalPageLayout';
import { SiteHeader } from '../components/layout';
import { Footer } from '../components/landing';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-serif text-stone-900 dark:text-stone-100 mb-3">{title}</h2>
      {children}
    </section>
  );
}

export function TermsPage() {
  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <SiteHeader mode="loading" />
      <LegalPageLayout title="Terms of Service" label="Legal" lastUpdated="March 19, 2026">
      <p>
        These Terms of Service ("Terms") govern your use of the Perch flight price tracking service
        ("Service") operated by Perch ("we," "us," or "our"). By using the Service, you agree to these Terms.
      </p>

      <Section title="Acceptance of Terms">
        <p>
          By creating an account or using Perch, you agree to be bound by these Terms and our{' '}
          <a href="/privacy" className="underline text-stone-900 dark:text-stone-100 hover:opacity-80">
            Privacy Policy
          </a>. If you do not agree, do not use the Service.
        </p>
      </Section>

      <Section title="Description of Service">
        <p>
          Perch tracks flight prices and award availability for bookings you add to the platform.
          We check prices periodically and alert you when we find cheaper options. You can manage
          your bookings, view price history, and receive notifications through the dashboard and
          email.
        </p>
      </Section>

      <Section title="Account Requirements">
        <ul className="list-disc pl-5 space-y-2">
          <li>You must sign in with a valid Google account.</li>
          <li>You are responsible for maintaining the security of your account.</li>
          <li>You must provide accurate booking information for price tracking to work correctly.</li>
          <li>You may only create one account per person.</li>
        </ul>
      </Section>

      <Section title="Acceptable Use">
        <p className="mb-3">You agree not to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Use the Service for any unlawful purpose.</li>
          <li>Attempt to access other users' data or accounts.</li>
          <li>Interfere with or disrupt the Service or its infrastructure.</li>
          <li>Use automated tools to scrape, crawl, or extract data from the Service.</li>
          <li>Resell, redistribute, or commercially exploit the Service without permission.</li>
        </ul>
      </Section>

      <Section title="Service Limitations">
        <p className="mb-3">Please be aware of the following limitations:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Beta status</strong> — Perch is currently
            in beta. Features may change, and service interruptions may occur.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Price accuracy</strong> — Prices and
            availability are sourced from third-party services and may not always reflect the exact fare
            available at the time of rebooking. We do not guarantee price accuracy.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Rebooking</strong> — When we find a lower
            price, we handle the rebooking on your behalf. Rebooking is subject to airline policies,
            fare rules, and availability. We cannot guarantee that every price drop can be captured.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Availability</strong> — We check prices
            periodically. We cannot guarantee that a price drop will be detected before it expires.
          </li>
        </ul>
      </Section>

      <Section title="Intellectual Property">
        <p>
          The Service, including its design, code, and content, is owned by Perch and protected by
          applicable intellectual property laws. You retain ownership of the booking data you provide.
        </p>
      </Section>

      <Section title="Pricing and Payments">
        <p>
          Perch is free to use. There is no subscription or upfront fee. You are only charged when Perch
          successfully rebooks your flight at a lower fare.
        </p>
        <p className="mt-2">
          <strong>Commission rate:</strong> 15% of confirmed savings on cash (non-award) bookings. "Confirmed
          savings" means the difference between your original fare and the rebooked fare, after the rebook is
          completed. Award/points bookings are not subject to any commission.
        </p>
        <p className="mt-2">
          <strong>Minimum threshold:</strong> Commission is only charged when savings are at least $25 USD and
          at least 5% of the original fare. Both conditions must be met.
        </p>
        <p className="mt-2">
          <strong>Payment method:</strong> A valid payment card is required to use the Service. Your card is
          stored securely by Stripe (our payment processor) and is charged automatically when a rebook occurs
          that meets the commission threshold. Perch does not store your full card number.
        </p>
        <p className="mt-2">
          <strong>Guarantee:</strong> If Perch finds no savings opportunities on your flights, you pay nothing.
          You are never charged for flights where no rebook occurred.
        </p>
        <p className="mt-2">
          <strong>Failed payments:</strong> If a commission charge fails (e.g., expired card), Perch will retry
          up to 3 times over 7 days. Your rebook is not reversed — you keep the savings regardless of payment
          status. Please update your payment method in Settings if notified of a failed charge.
        </p>
        <p className="mt-2">
          <strong>Disputes:</strong> If you believe a commission was charged incorrectly, contact us at the
          email below. We will review the savings calculation and issue a correction if warranted.
        </p>
      </Section>

      <Section title="Limitation of Liability">
        <p>
          To the fullest extent permitted by law, Perch shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages, including lost profits, arising from your use of
          the Service. Our total liability for any claim related to the Service shall not exceed the amount
          you have paid us in the twelve months preceding the claim.
        </p>
      </Section>

      <Section title="Account Termination">
        <p>
          You may request account deletion at any time from Settings &gt; Account. Deletion takes effect
          after a 30-day grace period during which you can cancel the request. We reserve the right to
          suspend or terminate accounts that violate these Terms.
        </p>
      </Section>

      <Section title="Changes to These Terms">
        <p>
          We may update these Terms from time to time. We will notify you of material changes by
          updating the "Last updated" date. Continued use of the Service after changes constitutes
          acceptance of the updated Terms.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          For questions about these Terms, contact us at{' '}
          <a
            href="mailto:saraswatayu@gmail.com"
            className="underline text-stone-900 dark:text-stone-100 hover:opacity-80"
          >
            saraswatayu@gmail.com
          </a>.
        </p>
      </Section>
      </LegalPageLayout>
      <Footer />
    </div>
  );
}
